/**
 * WaitlistAdmin — owner/admin review of inbound leads (Settings → Admin → Leads).
 *
 * Both the public "Request early access" (attorney) and the carrier "Become a client"
 * flows POST into db.waitlist; this is the first in-app way to SEE them. Read-only v1
 * (GET /api/v1/waitlist, admin-gated) — status management (mark invited/converted) is a
 * follow-up that needs a backend write endpoint. Carrier leads are distinguished by their
 * carrier-side role value (the `audience` tag isn't persisted by the current model).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Users } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';

interface WaitlistEntry {
  id: string;
  first_name?: string;
  last_name?: string;
  email: string;
  company?: string;
  role?: string;
  status?: string;
  created_at?: string;
}

const CARRIER_ROLES = new Set(['claims_leadership', 'adjuster', 'defense_counsel', 'siu']);
const ROLE_LABEL: Record<string, string> = {
  attorney: 'Attorney',
  accountant: 'CPA / Accountant',
  data_specialist: 'Data specialist',
  neutral: 'Neutral / Mediator',
  client: 'Client',
  other: 'Other',
  claims_leadership: 'Claims leadership',
  adjuster: 'Adjuster / examiner',
  defense_counsel: 'Defense counsel',
  siu: 'SIU / fraud',
};
const humanRole = (r?: string) => (r ? ROLE_LABEL[r] || r : '—');
const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

function StatusBadge({ status }: { status?: string }) {
  const s = (status || 'pending').toLowerCase();
  const color =
    s === 'converted' ? 'var(--color-verified)' : s === 'invited' ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)';
  return (
    <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-mono uppercase tracking-wide"
      style={{ color, borderColor: color }}>
      {s}
    </span>
  );
}

export default function WaitlistAdmin() {
  const { getToken } = useAuth();
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        // NOTE: the waitlist router is mounted UNVERSIONED (/api/waitlist) — same path
        // the public signup form POSTs to. /api/v1/waitlist 404s. Verified on deployed.
        const data = await api.get<{ total: number; entries: WaitlistEntry[] }>('/api/waitlist', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setEntries(data?.entries || []);
      } catch (e: any) {
        if (active) setError(e?.message || 'Could not load the waitlist.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getToken]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((e) =>
      `${e.first_name || ''} ${e.last_name || ''} ${e.email || ''} ${e.company || ''} ${humanRole(e.role)}`
        .toLowerCase()
        .includes(needle),
    );
  }, [entries, q]);

  const carrierCount = useMemo(() => entries.filter((e) => e.role && CARRIER_ROLES.has(e.role)).length, [entries]);

  return (
    <div className="space-y-6">
      {/* Plain section header — no orange fill (K3 gate: orange reserved for nav + primary action). */}
      <div className="flex items-center gap-3">
        <Users className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        <div>
          <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Leads</h2>
          <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>
            Inbound early-access + carrier "Become a client" requests
          </p>
        </div>
      </div>

      <div
        className="rounded-xl p-6"
        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
      >
        {/* Summary + search */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-6 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <span><strong style={{ color: 'var(--color-text-primary)' }}>{entries.length}</strong> total</span>
            <span><strong style={{ color: 'var(--color-needs-human-text)' }}>{carrierCount}</strong> carrier-side</span>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-dim)' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, firm, role…"
              className="pl-9 pr-3 py-2 rounded-lg text-sm w-72 max-w-full"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm" style={{ color: 'var(--color-status-adverse)' }}>{error}</p>
        ) : filtered.length === 0 ? (
          <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {entries.length === 0 ? 'No leads yet.' : 'No leads match your search.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: 'var(--color-text-dim)' }}>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4">Name</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4">Email</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4">Firm / org</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4">Role</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4">Status</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 text-right">Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const isCarrier = e.role && CARRIER_ROLES.has(e.role);
                  return (
                    <tr key={e.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                      <td className="py-3 pr-4" style={{ color: 'var(--color-text-primary)' }}>
                        {`${e.first_name || ''} ${e.last_name || ''}`.trim() || '—'}
                      </td>
                      <td className="py-3 pr-4" style={{ color: 'var(--color-text-secondary)' }}>{e.email}</td>
                      <td className="py-3 pr-4" style={{ color: 'var(--color-text-secondary)' }}>{e.company || '—'}</td>
                      <td className="py-3 pr-4" style={{ color: isCarrier ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)' }}>
                        {humanRole(e.role)}{isCarrier ? ' · carrier' : ''}
                      </td>
                      <td className="py-3 pr-4"><StatusBadge status={e.status} /></td>
                      <td className="py-3 text-right font-mono text-xs" style={{ color: 'var(--color-text-muted)' }}>{fmtDate(e.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
